import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth0 } from '../auth/DevAuth0Provider';
import { useCustomer } from '../../contexts/CustomerContext';
import { Icon } from '@iconify/react';
import { InfoPrompt } from '../common/InfoPrompts';
import { v4 as uuidv4 } from 'uuid';

const VoiceChatInterface = () => {
  // State management
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [transcript, setTranscript] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Refs for WebSocket and MediaRecorder
  const websocketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // Authentication and customer context
  const { getAccessTokenSilently } = useAuth0();
  const { currentCustomer, isImpersonating } = useCustomer();

  const audioContextRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const workletNodeRef = useRef(null);
  const playbackStartTimeRef = useRef(0);
  const leftoverByteRef = useRef(null);

  const processAudioQueue = useCallback(async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) {
        return;
    }

    isPlayingRef.current = true;
    const audioData = audioQueueRef.current.shift();
    
    // Track if this is the first audio chunk of an AI response
    const isFirstChunk = audioQueueRef.current.length === 0;

    try {
        // --- START: DEFINITIVE FIX ---
        // Immediately exit if the ArrayBuffer is not valid for Int16Array conversion.
        // This is the guard clause that prevents the RangeError.
        if (audioData.byteLength % 2 !== 0) {
            console.error(`Error: Received audio chunk with an invalid odd byte length (${audioData.byteLength}). Discarding chunk.`);
            isPlayingRef.current = false;
            processAudioQueue(); // Try the next chunk in the queue.
            return;
        }
        // --- END: DEFINITIVE FIX ---

        if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
        }

        // Use the actual sample rate from AudioContext instead of hardcoding
        const sampleRate = audioContextRef.current.sampleRate;
        const channelCount = 1;

        // If we've passed the check, this line is now guaranteed to be safe.
        const pcmData = new Int16Array(audioData);

        const audioBuffer = audioContextRef.current.createBuffer(
          channelCount,
          pcmData.length,
          sampleRate
        );

        const channelData = audioBuffer.getChannelData(0);

        for (let i = 0; i < pcmData.length; i++) {
            channelData[i] = pcmData[i] / 32768.0;
        }

        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContextRef.current.destination);

        const currentTime = audioContextRef.current.currentTime;
        const startTime = Math.max(currentTime, playbackStartTimeRef.current);
        source.start(startTime);

        playbackStartTimeRef.current = startTime + audioBuffer.duration;

        // When audio chunk finishes playing
        source.onended = () => {
            isPlayingRef.current = false;
            
            // Process the next chunk in the queue
            processAudioQueue();
            
            // If this was the last chunk and there are no more in the queue,
            // we can consider the AI's response complete
            if (audioQueueRef.current.length === 0) {
                // Add a newline to prepare for the next speaker
                setTranscript(prev => {
                    // Only add a newline if the transcript doesn't already end with one
                    // and if there's content in the transcript
                    if (prev.trim() && !prev.endsWith('\n')) {
                        return prev + '\n';
                    }
                    return prev;
                });
                
                // If the AI response doesn't have text yet, add a placeholder
                // This will help identify when AI speaks but we don't have transcription
                setTimeout(() => {
                    setTranscript(prev => {
                        if (prev.trim().endsWith('AI:')) {
                            // console.log('Adding placeholder for AI response');
                            return prev + ' [Audio response played]';
                        }
                        return prev;
                    });
                }, 500);
            }
        };
    } catch (error) {
        console.error('Error playing audio chunk:', error);
        isPlayingRef.current = false;
        processAudioQueue();
    }
  }, []);

  // Start call function
  const startCall = useCallback(async () => {
    try {
      setConnectionStatus('Connecting...');

      // Close existing AudioContext if it exists (we'll create a fresh one)
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close();
        } catch (e) {
          console.warn('Error closing existing AudioContext:', e);
        }
        audioContextRef.current = null;
      }
      
      // Create AudioContext first without specifying sampleRate - it will use system default
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      // Verify the AudioContext was created successfully
      if (!audioContextRef.current) {
        throw new Error('Failed to create AudioContext');
      }
      
      const audioContextSampleRate = audioContextRef.current.sampleRate;
      console.log('AudioContext sample rate:', audioContextSampleRate);
      
      // Request microphone access with the AudioContext's sample rate
      // This ensures the MediaStream matches the AudioContext
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: audioContextSampleRate, // Request the same rate as AudioContext
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        } 
      });
      mediaStreamRef.current = stream;
      
      // Verify the MediaStream's actual sample rate matches
      const audioTrack = stream.getAudioTracks()[0];
      const streamSampleRate = audioTrack?.getSettings()?.sampleRate;
      
      if (streamSampleRate) {
        console.log('MediaStream sample rate:', streamSampleRate);
        if (streamSampleRate !== audioContextSampleRate) {
          console.warn(`Sample rate mismatch detected: AudioContext=${audioContextSampleRate}Hz, MediaStream=${streamSampleRate}Hz`);
          // Close and recreate AudioContext with MediaStream's actual rate
          await audioContextRef.current.close();
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ 
            sampleRate: streamSampleRate 
          });
          console.log('Recreated AudioContext with sample rate:', streamSampleRate);
        }
      }

      // Load the worklet processor (always load for new AudioContext)
      await audioContextRef.current.audioWorklet.addModule('/recorder.worklet.js');

      // Create a source from the microphone stream
      const source = audioContextRef.current.createMediaStreamSource(stream);
      
      // Create the worklet node and assign it to the ref
      const workletNode = new AudioWorkletNode(audioContextRef.current, 'recorder-processor');
      workletNodeRef.current = workletNode;

      // Connect the microphone source to the worklet
      source.connect(workletNode);

      // Get authentication token
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });

      if (!token) {
        throw new Error('Failed to obtain authentication token');
      }

      // Check for impersonation state
      let customerIdParam = '';
      const savedState = sessionStorage.getItem('impersonation_state');
      if (savedState) {
        const state = JSON.parse(savedState);
        if (state.isImpersonating && state.currentCustomer?.id) {
          customerIdParam = `&customer_id=${encodeURIComponent(state.currentCustomer.id)}`;
        }
      }

      // Establish WebSocket connection
      // Generate a new session ID if one doesn't exist
      if (!sessionId) {
        setSessionId(uuidv4());
      }
      // Use the proxy backend URL instead of connecting directly to AI service
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://localhost:5000';
      const wsProtocol = backendUrl.includes('https://') ? 'wss://' : 'ws://';
      const wsHost = backendUrl.replace('https://', '').replace('http://', '');
      const wsUrl = `${wsProtocol}${wsHost}/api/web-voice?token=${encodeURIComponent(token)}${customerIdParam}&session_id=${encodeURIComponent(sessionId)}`;
      const websocket = new WebSocket(wsUrl);
      websocketRef.current = websocket;

      // Now that the worklet and websocket exist, set up the message handler
      let audioBuffer = new Int16Array(0);
      const CHUNK_SIZE_IN_SAMPLES = 5760;
      
      // For detecting user speech activity
      let silenceTimer = null;
      let isSpeechActive = false;
      let userSpeechDetected = false;
      
      workletNode.port.onmessage = (event) => {
          const newAudioData = new Int16Array(event.data);
          const combinedBuffer = new Int16Array(audioBuffer.length + newAudioData.length);
          combinedBuffer.set(audioBuffer);
          combinedBuffer.set(newAudioData, audioBuffer.length);
          audioBuffer = combinedBuffer;
          
          // Detect if user is speaking based on audio levels
          const isAudible = detectSpeech(newAudioData);
          
          if (isAudible && !isSpeechActive) {
              // User started speaking
              isSpeechActive = true;
              setIsSpeaking(true);
              userSpeechDetected = true;
              // console.log('User speech detected - starting');
              
              // Add a new line for user speech in transcript if not the first entry
              if (transcript.trim()) {
                  setTranscript(prev => {
                      if (!prev.includes('You:')) {
                          return prev + '\n\nYou: ';
                      }
                      return prev;
                  });
              } else {
                  setTranscript('You: ');
              }
              
              // Clear any existing silence timer
              if (silenceTimer) clearTimeout(silenceTimer);
          } 
          else if (isAudible && isSpeechActive) {
              // User is still speaking, reset silence timer
              if (silenceTimer) clearTimeout(silenceTimer);
              silenceTimer = setTimeout(() => {
                  // console.log('User speech detected - ended after silence');
                  isSpeechActive = false;
                  setIsSpeaking(false);
              }, 1500); // 1.5 seconds of silence to consider speech ended
          }

          // Send chunks of the correct size
          while (audioBuffer.length >= CHUNK_SIZE_IN_SAMPLES) {
              const chunkToSend = audioBuffer.slice(0, CHUNK_SIZE_IN_SAMPLES);
              audioBuffer = audioBuffer.slice(CHUNK_SIZE_IN_SAMPLES);

              if (websocket.readyState === WebSocket.OPEN) {
                  // Create a JSON object with session ID and audio data
                  const reader = new FileReader();
                  const blob = new Blob([chunkToSend.buffer], { type: 'audio/raw' });
                  
                  reader.onload = function() {
                    const base64AudioData = reader.result.split(',')[1]; // Remove the data URL prefix
                    const payload = {
                      sessionId: sessionId,
                      audioData: base64AudioData
                    };
                    
                    // Log payload details for debugging (without the full audio data)
                    // console.log('Sending WebSocket payload:', {
                    //   sessionId: sessionId,
                    //   payloadType: 'audio',
                    //   audioDataSize: base64AudioData.length,
                    //   timestamp: new Date().toISOString()
                    // });
                    
                    websocket.send(JSON.stringify(payload));
                  };
                  
                  reader.readAsDataURL(blob);
              }
          }
      };
      
      // Function to detect speech activity from audio data
      const detectSpeech = (audioData) => {
          // Calculate RMS (Root Mean Square) of the audio data
          let sum = 0;
          for (let i = 0; i < audioData.length; i++) {
              sum += audioData[i] * audioData[i];
          }
          const rms = Math.sqrt(sum / audioData.length);
          
          // Lower threshold for speech detection to be more sensitive
          const threshold = 500; // Lowered from 1000 to be more sensitive
          return rms > threshold;
      };

      // WebSocket event handlers
      websocket.onopen = () => {
        setConnectionStatus('Listening');
        // console.log('WebSocket connection established', {
        //   url: wsUrl,
        //   sessionId: sessionId,
        //   timestamp: new Date().toISOString(),
        //   customerImpersonation: !!customerIdParam
        // });
      };

      websocket.onmessage = async (event) => {
        // Log incoming messages (with type information)
        // console.log('Received WebSocket message:', {
        //   type: event.data instanceof Blob ? 'Blob' : 
        //         event.data instanceof ArrayBuffer ? 'ArrayBuffer' : 
        //         typeof event.data,
        //   size: event.data instanceof Blob || event.data instanceof ArrayBuffer ? 
        //         (event.data.size || event.data.byteLength) : 
        //         (typeof event.data === 'string' ? event.data.length : 'unknown'),
        //   timestamp: new Date().toISOString(),
        //   sessionId: sessionId
        // });
        
        // If we receive binary audio data (the AI's voice)
        if (event.data instanceof Blob || event.data instanceof ArrayBuffer) {
          const arrayBuffer = event.data instanceof Blob ? await event.data.arrayBuffer() : event.data;
  
          // Add this audio chunk to the queue for playback
          audioQueueRef.current.push(arrayBuffer);
          processAudioQueue();
          
          // If this is the first audio chunk of an AI response, add an "AI:" label to the transcript
          // console.log('Received AI audio chunk, isPlaying:', isPlayingRef.current, 'queue length:', audioQueueRef.current.length);
          
          // Always add an AI label when receiving audio, as this is definitely an AI response
          if (transcript.trim()) {
            // Check if we already have an AI label at the end
            if (!transcript.trim().endsWith('AI:')) {
              // console.log('Adding AI label to transcript');
              setTranscript(prev => prev + '\n\nAI: ');
            }
          } else {
            // console.log('Setting initial AI label');
            setTranscript('AI: ');
          }
  
          return; // End execution for this audio chunk
        } 
    
        // Handle text/JSON messages
        if (typeof event.data === 'string') {
          try {
            // console.log('Received text message:', event.data);
            const jsonData = JSON.parse(event.data);
            // console.log('Parsed JSON data:', jsonData);
            
            if (jsonData.type === 'error') {
              // Handle error messages
              console.error('Backend error:', jsonData.message);
              setConnectionStatus('Error: ' + jsonData.message);
              setTranscript(prev => prev + `\n[ERROR]: ${jsonData.message}\n`);
            } 
            else if (jsonData.type === 'transcript' || jsonData.transcript) {
              // Handle transcript from user speech recognition
              // console.log('Received transcript data:', jsonData);
              const transcriptText = jsonData.transcript || jsonData.text || jsonData.message || '';
              // console.log('Extracted transcript text:', transcriptText);
              
              if (transcriptText && transcriptText.trim()) {
                // Append to the current speaker's text (should be "You:")
                setTranscript(prev => {
                  // If we already have a "You:" label, just append the text
                  if (prev.trim().endsWith('You:')) {
                    return prev + ' ' + transcriptText;
                  } 
                  // If we're in the middle of user speech, just append
                  else if (isSpeaking) {
                    return prev + ' ' + transcriptText;
                  }
                  // Otherwise, add a new "You:" label
                  else {
                    return prev + (prev.trim() ? '\n\nYou: ' : 'You: ') + transcriptText;
                  }
                });
              }
            } 
            else if (jsonData.type === 'status') {
              setConnectionStatus(jsonData.message || jsonData.status);
            } 
            else if (jsonData.type === 'connection') {
              // console.log('Connection message:', jsonData.message);
            } 
            else if (jsonData.type === 'ai_response' || jsonData.ai_response) {
              // Handle text responses from AI (not audio)
              // console.log('Received AI response data:', jsonData);
              const aiText = jsonData.ai_response || jsonData.text || jsonData.message || '';
              // console.log('Extracted AI text:', aiText);
              
              if (aiText && aiText.trim()) {
                setTranscript(prev => {
                  // If we already have an "AI:" label, just append the text
                  if (prev.trim().endsWith('AI:')) {
                    return prev + ' ' + aiText;
                  } 
                  // Otherwise, add a new "AI:" label
                  else {
                    return prev + (prev.trim() ? '\n\nAI: ' : 'AI: ') + aiText;
                  }
                });
              }
            }
            else {
              // Handle any other text data that might contain transcriptions
              // console.log('Received other JSON data:', jsonData);
              
              // Check all possible fields that might contain transcription data
              let textData = '';
              
              if (jsonData.text) textData = jsonData.text;
              else if (jsonData.message) textData = jsonData.message;
              else if (jsonData.transcription) textData = jsonData.transcription;
              else if (jsonData.data && typeof jsonData.data === 'string') textData = jsonData.data;
              else if (jsonData.content) textData = jsonData.content;
              else if (jsonData.speech_text) textData = jsonData.speech_text;
              else if (jsonData.user_speech) textData = jsonData.user_speech;
              
              // console.log('Extracted text from other fields:', textData);
              
              if (textData && textData.trim()) {
                // If we're currently speaking, assume this is user speech
                if (isSpeaking) {
                  setTranscript(prev => {
                    if (prev.trim().endsWith('You:')) {
                      return prev + ' ' + textData;
                    } else {
                      return prev + (prev.trim() ? '\n\nYou: ' : 'You: ') + textData;
                    }
                  });
                } else {
                  // Otherwise just append it
                  setTranscript(prev => prev + ' ' + textData);
                }
              }
            }
          } catch (parseError) {
            // console.log('Failed to parse JSON, treating as plain text:', event.data);
            // Handle plain text (non-JSON) messages
            if (event.data.trim()) {
              setTranscript(prev => prev + ' ' + event.data);
            }
          }
        } else {
          // console.log('Received unknown data type:', typeof event.data, event.data);
        }
      };

      websocket.onclose = () => {
        setConnectionStatus('Disconnected');
        setIsRecording(false);
        // console.log('WebSocket connection closed');
        cleanupResources();
      };

      websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('Disconnected');
        setIsRecording(false);
        cleanupResources();
      };

      setIsRecording(true);

    } catch (error) {
      console.error('Error starting call:', error);
      setConnectionStatus('Disconnected');
      cleanupResources();
      
      if (error.name === 'NotAllowedError') {
        alert('Microphone access denied. Please allow microphone access and try again.');
      } else if (error.name === 'NotFoundError') {
        alert('No microphone found. Please connect a microphone and try again.');
      } else if (error.message.includes('authentication') || error.message.includes('token')) {
        alert('Authentication failed. Please log in again and try again.');
        console.error('Authentication error for WebSocket:', error);
      } else {
        alert('Failed to start call. Please check your connection and try again.');
      }
    }
  }, [getAccessTokenSilently, processAudioQueue, sessionId]);

  // Stop call function
  const stopCall = useCallback(() => {
    setIsRecording(false);
    setConnectionStatus('Disconnected');
    setIsSpeaking(false);

    // Add a separator in the transcript to indicate the end of the session
    setTranscript(prev => {
      if (prev.trim()) {
        return prev + '\n\n--- Session Ended ---\n\n';
      }
      return prev;
    });

    // Disconnect and remove the worklet node
    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage('stop'); // Optional: send a stop message
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    // Stop MediaRecorder
    // if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
    //   mediaRecorderRef.current.stop();
    // }

    // Close WebSocket connection
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      websocketRef.current.close();
    }

    cleanupResources();
  }, []);

  // Cleanup function
  const cleanupResources = useCallback(() => {
    // Release microphone track
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Reset refs
    mediaRecorderRef.current = null;
    websocketRef.current = null;
  }, []);

  // Generate session ID on component mount
  useEffect(() => {
    const newSessionId = uuidv4();
    setSessionId(newSessionId);
    // console.log('Generated new session ID:', newSessionId);
  }, []);

  // Cleanup on component unmount
  React.useEffect(() => {
    return () => {
      stopCall();
    };
  }, [stopCall]);

  // Clear transcript function
  const clearTranscript = () => {
    setTranscript('');
  };

  return (
    <div className="voice-chat-interface flex flex-col gap-4 p-6 h-full w-full mx-auto rounded-lg responsive-page-layout">
      {/* Maintenance Prompt */}
      {/* <InfoPrompt 
        description="The voice chat is working as a demo for the moment. But the context is not stored right now. This will be updated in the next patch."
        icon="material-symbols:warning-rounded"
      /> */}
      <div className="flex flex-row justify-between items-center w-full">
        {/* Title */}
        <div className="header-textbox">
            <Icon icon="material-symbols:voice-chat-outline-rounded" className="header-icon"></Icon>
            Voice Chat
        </div>
        {/* Control Button */}
        <button
          onClick={isRecording ? stopCall : startCall}
          disabled={connectionStatus === 'Connecting...'}
          className={`px-6 py-3 rounded-lg font-semibold group transition-all duration-200 ${
            isRecording 
              ? 'bg-red-500 hover:bg-red-600 focus:ring-red-300' 
              : 'bg-primary text-background hover:bg-bgSecondary hover:text-primary hover:shadow-button'
          } ${
            connectionStatus === 'Connecting...' 
              ? 'opacity-50 cursor-not-allowed' 
              : 'focus:outline-none focus:ring-2'
          }`}
        >
          {connectionStatus === 'Connecting...' ? 'Connecting...' : (isRecording ? 'Stop Call' : 'Start Call')}
        </button>
      </div>

      {/* Connection Status */}
      <div className="mb-4 w-full">
        <p className="text-sm font-medium text-font-secondary mb-2">Status:</p>
        <div className={`px-3 py-2 rounded-md text-sm font-medium ${
          connectionStatus === 'Listening' 
            ? 'bg-green-100 text-green-800' 
            : connectionStatus === 'Connecting...'
            ? 'bg-yellow-100 text-yellow-800'
            : 'card-container rounded-md text-font-secondary'
        }`}>
          {connectionStatus}
        </div>
      </div>

      {/* Recording Indicator */}
      {isRecording && (
        <div className="flex items-center justify-center space-x-2 text-error">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          <span className="text-xs font-medium">Recording...</span>
        </div>
      )}
    </div>
  );
};


export default VoiceChatInterface;